import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { sendEmailVerification } from 'firebase/auth';
import { useAuth } from '../../context/AuthContext';
import logger from '../../utils/logger';
import {
  acceptInviteByToken,
  previewAdvisorInvite,
  ADVISOR_EMAIL_INVITE_ENABLED,
} from '../../api/advisorInviteApi';
import { getPendingInviteToken, clearPendingInviteToken } from '../../utils/advisorInviteToken';
import {
  KANZLEI_ONBOARDING_DOC_PATH,
  LEGAL_DRAFT_MARKER,
} from '../../config/advisorConsentAccess';
import { useLegalDocument } from './useLegalDocument';
import { readOwnOnboardingStatus } from '../../utils/advisorOnboarding';
import KanzleiOnboardingDialog from './KanzleiOnboardingDialog';

/**
 * M11b-4 — Annahme-Sicht für Token-Einladungen (GATED).
 *
 * Liest den Token aus dem URL-Parameter `?invite=<token>` (der Mail-Link führt
 * `REGISTER_URL?invite=<token>` → nach Registrierung/Login auf die Steuerberater-
 * Seite). Der Token wird NIE im Klartext in Firestore geschrieben — die Einlösung
 * passiert server-seitig in {@link acceptInviteByToken}.
 *
 * Verifikations-Gate (A1, re-invokebar A2): Ist die E-Mail des eingeloggten Users
 * noch nicht bestätigt (`emailVerified===false`), zeigt das Panel einen Hinweis +
 * „Bestätigungsmail erneut senden" + „Erneut prüfen" und ruft die Callable bewusst
 * NICHT auf. Der Server würde unverifiziert ohnehin `failed-precondition` werfen und
 * die Einladung `pending` lassen. Nach Verifikation kann der Nutzer „Annehmen"
 * beliebig oft auslösen, bis der Invite eingelöst oder abgelaufen ist.
 *
 * Rendert nichts, wenn das Flag off ist, kein Token in der URL steht oder kein User
 * eingeloggt ist.
 */
const AdvisorInviteAcceptPanel: React.FC = () => {
  const { t } = useTranslation('common');
  const { currentUser, reloadTenants, switchTenant, saveSellerData } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Token from URL query param (primary) with localStorage fallback (M11b carry
  // mechanism: RegisterPage/LoginPage writes the token so it survives the auth
  // redirect chain even when the query param is no longer present).
  const token = (searchParams.get('invite') || getPendingInviteToken() || '').trim();

  const [working, setWorking] = useState(false);
  const [verified, setVerified] = useState<boolean>(!!currentUser?.emailVerified);
  const [result, setResult] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);
  // M11b Portal-Fix (Bug 2): der einladende Mandanten-Anzeigename, on-mount read-only
  // geladen. null = nicht auflösbar → generischer Text ohne Platzhalter-Leak.
  const [mandantName, setMandantName] = useState<string | null>(null);
  // M11b Portal-Fix (Bug 3 Re-Loop): Erfolgs-Latch. `token` wird bei JEDEM Render neu
  // aus searchParams/localStorage berechnet und verschwindet nach `clearPendingInvite
  // Token()` (insbesondere im localStorage-Fallback-Fall, wo kein URL-Param übrig
  // bleibt). Ohne dieses Latch würde die Komponente direkt nach erfolgreicher Annahme
  // wieder `null` rendern und die Erfolgsmeldung („Sie haben jetzt Lesezugriff …" +
  // Umschalter-Hinweis) wäre NIE sichtbar (exakt Daniels Beschwerde #3). Einmal auf
  // `true` gesetzt, hält dieses Latch die Erfolgs-Sektion sichtbar UNABHÄNGIG vom
  // `active`-Token-Guard.
  const [accepted, setAccepted] = useState(false);
  // §5-Onboarding (B4): Dialog VOR dem Token-Accept, wenn die eigene Bestätigung
  // fehlt/veraltet ist. Nur relevant, wenn die §5-Datei freigegeben (`ready`) ist.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingDoc = useLegalDocument(KANZLEI_ONBOARDING_DOC_PATH, LEGAL_DRAFT_MARKER);

  const active = ADVISOR_EMAIL_INVITE_ENABLED && !!token && !!currentUser;

  // M11b Portal-Fix (Bug 2): Namen des einladenden Mandanten laden, sobald ein Token
  // vorliegt. Rein lesende Callable; jeder Fehler → null (generischer Fallback-Text).
  useEffect(() => {
    if (!active) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { mandantName: name } = await previewAdvisorInvite(token);
        if (!cancelled) {
          setMandantName(name);
        }
      } catch (err) {
        logger.error('[AdvisorInviteAcceptPanel] preview failed', err);
        if (!cancelled) {
          setMandantName(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, token]);

  // Inert / nichts zu tun — AUSSER das Erfolgs-Latch ist gesetzt: eine bereits
  // erfolgreich angenommene Einladung bleibt sichtbar, auch wenn `token` inzwischen
  // (durch clearPendingInviteToken) leer geworden ist.
  if (!accepted && (!active || !currentUser)) {
    return null;
  }

  const handleResendVerification = async () => {
    // Re-Loop-Fix: currentUser kann theoretisch null sein, wenn `accepted` das Panel
    // gemountet hält (Latch), obwohl der Inert-Guard oben currentUser NICHT mehr
    // erzwingt. Dieser Zweig wird UI-seitig ohnehin nur vor Erfolg gerendert; der
    // Guard dient rein der TS-Nullability und dem defensiven Fail-Safe.
    if (!currentUser) return;
    setResult(null);
    setWorking(true);
    try {
      await sendEmailVerification(currentUser);
      setResult({ type: 'info', message: t('tenant.acceptVerificationSent') });
    } catch (err) {
      logger.error('[AdvisorInviteAcceptPanel] resend verification failed', err);
      setResult({ type: 'error', message: t('tenant.acceptError') });
    } finally {
      setWorking(false);
    }
  };

  const handleRecheck = async () => {
    // Re-Loop-Fix: siehe Null-Guard-Begründung in handleResendVerification oben.
    if (!currentUser) return;
    setResult(null);
    setWorking(true);
    try {
      await currentUser.reload();
      const nowVerified = !!currentUser.emailVerified;
      setVerified(nowVerified);
      if (!nowVerified) {
        setResult({ type: 'info', message: t('tenant.acceptStillUnverified') });
      }
    } catch (err) {
      logger.error('[AdvisorInviteAcceptPanel] reload failed', err);
      setResult({ type: 'error', message: t('tenant.acceptError') });
    } finally {
      setWorking(false);
    }
  };

  // Klick auf „Annehmen": §5-Onboarding-Vorprüfung nur bei freigegebener §5-Datei
  // erzwingen; fehlt die eigene Bestätigung ⇒ §5-Dialog VOR dem Accept. Sonst inert.
  const handleAcceptClick = async () => {
    if (onboardingDoc.kind === 'ready' && currentUser?.uid) {
      setWorking(true);
      const status = await readOwnOnboardingStatus(currentUser.uid);
      setWorking(false);
      if (!status.confirmed) {
        setResult(null);
        setShowOnboarding(true);
        return;
      }
    }
    await handleAccept();
  };

  const handleAccept = async () => {
    setResult(null);
    setWorking(true);
    try {
      // 1. Annehmen — Server liefert tenantId (= scopeUid) + Mandanten-Name zurück.
      //    Kein consentAccepted-Arg (Richtung a2): der Consent wurde beim Absenden
      //    erfasst und liegt server-seitig bereits auf dem Invite-Doc.
      const { tenantId, mandantName: resolvedName } = await acceptInviteByToken(token);
      // 2. localStorage-Token räumen, damit der MainLayout-Redirect nicht erneut feuert.
      clearPendingInviteToken();
      // 3. Bug-1-Residual: sticky Register-Hint löschen (best-effort — Fehler nicht
      //    den Flow abbrechen lassen; ein Dual-Role-User bekäme sonst dauerhaft den
      //    eigenen Onboarding-Wizard unterdrückt).
      try {
        await saveSellerData({ pendingAdvisorInviteHint: false });
      } catch (hintErr) {
        logger.warn('[AdvisorInviteAcceptPanel] could not clear pendingAdvisorInviteHint', hintErr);
      }
      // 4. Tenant-Listen neu laden — danach ist der Mandant switch-bar (availableTenants
      //    Länge 2 → TenantSwitcher sichtbar) und der Invite aus pendingInvites raus.
      await reloadTenants();
      // 5. In den Mandanten-Tenant wechseln — greift, da availableTenantsRef frisch ist.
      //    Passiert VOR dem Sichtbarmachen der Erfolgsmeldung (Spec 3.3-Reihenfolge).
      switchTenant(tenantId);
      // 6. Erfolgs-UX: mit Namen die Mandanten-Meldung + Umschalter-Hinweis, sonst Fallback.
      //    Re-Loop-Fix: KEIN sofortiger navigate() mehr im selben Batch — der Nutzer
      //    soll die Meldung tatsächlich SEHEN. Das Erfolgs-Latch (`accepted`) hält die
      //    Sektion sichtbar, auch wenn `token` danach leer wird. Der Wechsel ins
      //    Dashboard erfolgt über einen expliziten CTA-Button (deterministisch, kein
      //    Timer-Flake) statt eines automatischen Redirects.
      const displayName = resolvedName ?? mandantName;
      setMandantName(displayName);
      const successMessage = displayName
        ? `${t('tenant.acceptSuccessMandant', { mandant: displayName })} ${t('tenant.acceptSwitcherHint', { mandant: displayName })}`
        : t('tenant.acceptSuccess');
      setResult({ type: 'success', message: successMessage });
      setAccepted(true);
    } catch (err) {
      const code = (err as { code?: string })?.code;
      const message = (err as { message?: string })?.message ?? '';
      // A1 email mismatch: the logged-in user's email differs from the invited
      // address. Server returns permission-denied (never not-found — anti-enum A5).
      if (code === 'permission-denied') {
        setResult({ type: 'error', message: t('tenant.acceptEmailMismatch') });
      } else if (
        code === 'failed-precondition' &&
        (message === 'onboarding_required' || message === 'onboarding_stale')
      ) {
        // B4: Kanzlei-Onboarding fehlt/veraltet ⇒ §5-Dialog anbieten (statt Fehler).
        setShowOnboarding(true);
      } else if (code === 'failed-precondition' && message === 'consent_missing') {
        // B3 (a2): der Consent fehlt auf einem Alt-Invite ⇒ neu versenden lassen.
        setResult({ type: 'error', message: t('advisorConsent.consentMissingNotice') });
      } else if (code === 'failed-precondition') {
        // Server lehnt unverifizierte Annahme mit failed-precondition ab — Hinweis zeigen.
        setVerified(false);
        setResult({ type: 'info', message: t('tenant.acceptVerifyEmailNotice') });
      } else {
        logger.error('[AdvisorInviteAcceptPanel] accept failed', err);
        setResult({ type: 'error', message: t('tenant.acceptError') });
      }
    } finally {
      setWorking(false);
    }
  };

  // Re-Loop-Fix: das Latch ist der robuste Erfolgs-Indikator (unabhängig davon, ob
  // `result` je aus anderen Gründen zurückgesetzt würde).
  const isSuccess = accepted;

  return (
    <section className="mb-6 rounded-lg bg-surface-raised ring-1 ring-indigo-500/30 p-4">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-accent-ink mb-2">
        {t('tenant.acceptTitle')}
      </h2>

      {/* M11b Portal-Fix (Bug 2): welcher Mandant eingeladen hat — in allen Branches,
          wenn auflösbar. Bei null bleibt es beim generischen Text darunter. */}
      {!isSuccess && mandantName && (
        <p className="text-sm text-[var(--tone-accent-fg)] leading-relaxed mb-2">
          {t('tenant.acceptFromMandant', { mandant: mandantName })}
        </p>
      )}

      {isSuccess ? (
        <div>
          <p className="text-sm text-[var(--tone-success-fg)]">{result?.message}</p>
          {/* Re-Loop-Fix: expliziter CTA statt automatischem Redirect — der Tenant
              wurde bereits in handleAccept gewechselt (switchTenant), dieser Button
              navigiert nur zur Ansicht. Deterministisch/testbar, kein Timer-Flake. */}
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500"
          >
            {mandantName
              ? t('tenant.acceptGoToMandant', { mandant: mandantName })
              : t('tenant.acceptGoToDashboard')}
          </button>
        </div>
      ) : !verified ? (
        <div>
          <p className="text-sm text-secondary leading-relaxed">{t('tenant.acceptVerifyEmailNotice')}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={working}
              className="inline-flex items-center rounded-md bg-surface-inset px-3 py-1.5 text-xs font-medium text-secondary hover:bg-surface-hover disabled:opacity-50"
            >
              {t('tenant.acceptResendVerification')}
            </button>
            <button
              type="button"
              onClick={handleRecheck}
              disabled={working}
              className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
            >
              {working ? t('tenant.pendingWorking') : t('tenant.acceptRecheck')}
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm text-secondary leading-relaxed">{t('tenant.acceptReadyNotice')}</p>
          <button
            type="button"
            onClick={handleAcceptClick}
            disabled={working}
            className="mt-3 inline-flex items-center rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            {working ? t('tenant.acceptWorking') : t('tenant.acceptButton')}
          </button>
        </div>
      )}

      {/* §5-Kanzlei-Onboarding-Dialog (B4) VOR dem Token-Accept — nur bei
          freigegebener §5-Datei und fehlender/veralteter eigener Bestätigung. */}
      {showOnboarding && (
        <KanzleiOnboardingDialog
          onConfirmed={() => {
            setShowOnboarding(false);
            void handleAccept();
          }}
          onCancel={() => setShowOnboarding(false)}
        />
      )}

      {result && !isSuccess && (
        <p
          className={`mt-2 text-xs ${
            result.type === 'error' ? 'text-[var(--tone-danger-fg)]' : 'text-muted'
          }`}
        >
          {result.message}
        </p>
      )}
    </section>
  );
};

export default AdvisorInviteAcceptPanel;
